import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  Segmented,
  Spinner,
  Table,
  type BadgeVariant,
  type TableColumn,
} from "../../ds";
import { api, errorText, type Capture, type DeviceDetail, type NetworkStatus } from "../../lib/api";
import { fmtBytes, fmtDateTime } from "../../lib/format";

// Tab „Mitschnitt“: tcpdump auf der Box, .pcap.gz danach auf dem Server.
// Bewusst ohne SectionCard — es gibt keinen Router-Snapshot dahinter, nur den
// Server-Katalog (`GET /api/captures`). Solange einer läuft, pollt diese Ansicht
// den SERVER; der Router wird dabei nicht angefasst (docs/device-management.md
// → „Mitschnitte“).

const DEFAULT_IFACE = "br-lan";
const POLL_MS = 3000;
/** Vorgaben: knapp. Ein Mitschnitt ist der teuerste Vorgang auf einer SIM. */
const DEFAULTS = { filter: "", snaplen: 128, maxSecs: 60, maxMb: 5 };
const MAX_MB = 20;

const STATUS: Record<Capture["status"], { variant: BadgeVariant; label: string }> = {
  running: { variant: "warn", label: "läuft" },
  ok: { variant: "success", label: "fertig" },
  error: { variant: "danger", label: "fehlerhaft" },
};

/** Interfaces zur Auswahl: was der Kernel hat (aus dem gecachten Netzwerk-Stand),
 *  dazu die Bridge als Vorgabe und `any`. Kein Abruf — die Liste darf keinen
 *  Funkverkehr kosten. */
function ifaceOptions(device: DeviceDetail): string[] {
  const network = device.sections.network?.data as NetworkStatus | undefined;
  const fromRouter = (network?.interfaces ?? [])
    .map((i) => i.l3Device || i.device)
    .filter((name): name is string => !!name);
  return [...new Set([DEFAULT_IFACE, ...fromRouter, "any"])];
}

function captureColumns(
  onDelete: (capture: Capture) => void,
  busy: boolean,
): TableColumn<Capture>[] {
  return [
    { key: "createdAt", header: "Zeitpunkt", cell: (c) => fmtDateTime(c.createdAt) },
    { key: "iface", header: "Interface", mono: true, cell: (c) => c.iface || "—" },
    {
      key: "filter",
      header: "Filter",
      mono: true,
      hideOnMobile: true,
      cell: (c) => c.bpfFilter || "alles",
    },
    {
      key: "umfang",
      header: "Umfang",
      hideOnMobile: true,
      cell: (c) => (c.snaplen === 0 ? "ganze Pakete" : `${c.snaplen} B/Paket`),
    },
    {
      key: "size",
      header: "Größe",
      num: true,
      cell: (c) => (c.status === "ok" ? fmtBytes(c.sizeBytes) : "—"),
    },
    {
      key: "packets",
      header: "Pakete",
      num: true,
      hideOnMobile: true,
      cell: (c) =>
        c.status === "ok" ? (
          <span title={c.dropped ? `${c.dropped} vom Kernel verworfen` : undefined}>
            {c.packets}
            {c.dropped > 0 && <span className="text-warn"> (−{c.dropped})</span>}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "status",
      header: "Status",
      cell: (c) => (
        <span className="flex items-center gap-2">
          <Badge variant={STATUS[c.status].variant}>{STATUS[c.status].label}</Badge>
          {c.truncated && <Badge variant="neutral">Limit erreicht</Badge>}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "actions",
      cell: (c) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            iconName="download"
            disabled={c.status !== "ok"}
            title="Entschlüsselt vom Server herunterladen (.pcap.gz)"
            onClick={() => window.location.assign(api.captureDownloadUrl(c.id))}
          >
            Download
          </Button>
          <Button
            size="sm"
            variant="ghost"
            iconName="trash"
            disabled={busy || c.status === "running"}
            onClick={() => onDelete(c)}
          >
            Löschen
          </Button>
        </div>
      ),
    },
  ];
}

export function CaptureSection({ device }: { device: DeviceDetail }) {
  const [rows, setRows] = useState<Capture[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<Capture | null>(null);

  const [iface, setIface] = useState(DEFAULT_IFACE);
  const [filter, setFilter] = useState(DEFAULTS.filter);
  const [wholePackets, setWholePackets] = useState(false);
  const [maxSecs, setMaxSecs] = useState(String(DEFAULTS.maxSecs));
  const [maxMb, setMaxMb] = useState(String(DEFAULTS.maxMb));

  const options = useMemo(() => ifaceOptions(device), [device]);
  const running = rows?.find((c) => c.status === "running") ?? null;

  // Reiner Server-Katalog — kostet kein Router-Volumen.
  const load = useCallback(
    () =>
      api
        .listCaptures(device.id)
        .then((c) => {
          setRows(c);
          setError(null);
        })
        .catch((e: unknown) => {
          setRows([]);
          setError(errorText(e));
        }),
    [device.id],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Solange einer läuft, nachsehen, ob die Datei da ist. Der Router meldet das
  // nicht über den WS — fertig ist der Mitschnitt, wenn er hochgeladen hat.
  const runningId = running?.id ?? null;
  useEffect(() => {
    if (!runningId) return;
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [runningId, load]);

  async function act(run: () => Promise<unknown>, okText: string) {
    setBusy(true);
    setError(null);
    try {
      await run();
      setNote(okText);
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
      setDeleting(null);
    }
  }

  function start() {
    void act(
      () =>
        api.startCapture(device.id, {
          iface,
          filter: filter.trim() || undefined,
          snaplen: wholePackets ? 0 : DEFAULTS.snaplen,
          max_secs: Number(maxSecs) || DEFAULTS.maxSecs,
          max_bytes: (Number(maxMb) || DEFAULTS.maxMb) * 1024 * 1024,
        }),
      "Mitschnitt läuft. Er endet mit „Stop“, beim Zeitlimit oder beim Größenlimit.",
    );
  }

  return (
    <>
      <Card
        title="Mitschnitt (tcpdump)"
        subtitle="Läuft auf dem Router, landet komprimiert auf dem Server. Der Upload geht über die SIM — Vorgaben sind deshalb knapp."
        actions={
          running ? (
            <Button
              size="sm"
              variant="danger-outline"
              iconName="stop"
              loading={busy}
              disabled={!device.online}
              onClick={() =>
                void act(
                  () => api.stopCapture(device.id),
                  "Gestoppt. Der Router lädt die Datei jetzt hoch.",
                )
              }
            >
              Stop
            </Button>
          ) : (
            <Button
              size="sm"
              variant="primary"
              iconName="play"
              loading={busy}
              disabled={!device.online}
              title={device.online ? undefined : "Router ist offline"}
              onClick={start}
            >
              Start
            </Button>
          )
        }
      >
        <div className="flex flex-col gap-4">
          {!device.online && (
            <Alert variant="neutral">
              Router offline — ein Mitschnitt lässt sich nur starten, solange er hängt.
            </Alert>
          )}
          {error && <Alert variant="danger">{error}</Alert>}
          {note && (
            <Alert variant="success" dismissible onDismiss={() => setNote(null)}>
              {note}
            </Alert>
          )}
          {running && (
            <Alert variant="info">
              Läuft seit {fmtDateTime(running.createdAt)} auf <code>{running.iface}</code> — endet
              spätestens nach {running.maxSecs} s oder bei {fmtBytes(running.maxBytes)}. Die Zeile
              wird „fertig“, sobald die Datei auf dem Server liegt.
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label="Interface"
              htmlFor="cap-iface"
              helperText="Vorgabe ist die LAN-Bridge. `any` schneidet alle Interfaces mit."
            >
              <select
                id="cap-iface"
                className="ds-input"
                value={iface}
                disabled={!!running}
                onChange={(e) => setIface(e.target.value)}
              >
                {options.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </Field>
            <Input
              label="Filter (BPF)"
              placeholder="z.B. port 53 or icmp"
              helperText="Leer = alles. Syntax prüft der Router vor dem Start."
              value={filter}
              disabled={!!running}
              onChange={(e) => setFilter(e.target.value)}
            />
            <Field
              label="Umfang je Paket"
              helperText={
                wholePackets
                  ? "Ganze Pakete: vielfaches Volumen, und auf ausgelasteten Links verwirft der Kernel Pakete."
                  : "Nur die ersten 128 Byte — Header reichen für die meisten Fragen."
              }
            >
              <Segmented
                options={[
                  { value: "header", label: "Nur Header" },
                  { value: "voll", label: "Ganze Pakete" },
                ]}
                value={wholePackets ? "voll" : "header"}
                onChange={(v) => !running && setWholePackets(v === "voll")}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Zeitlimit (s)"
                type="number"
                min={5}
                max={900}
                helperText="5–900"
                value={maxSecs}
                disabled={!!running}
                onChange={(e) => setMaxSecs(e.target.value)}
              />
              <Input
                label="Größenlimit (MB)"
                type="number"
                min={1}
                max={MAX_MB}
                helperText={`1–${MAX_MB}`}
                value={maxMb}
                disabled={!!running}
                onChange={(e) => setMaxMb(e.target.value)}
              />
            </div>
          </div>
        </div>
      </Card>

      <Card
        title="Mitschnitte auf dem Server"
        subtitle="Verschlüsselt abgelegt und nach 7 Tagen automatisch gelöscht — sie enthalten fremden Netzverkehr."
        actions={
          <Button size="sm" variant="outline" iconName="refresh" onClick={() => void load()}>
            Neu laden
          </Button>
        }
        bodyPadding={false}
      >
        <div className="flex flex-col gap-3 p-4">
          {rows === null ? (
            <div className="flex justify-center p-8">
              <Spinner />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              iconName="activity"
              title="Noch kein Mitschnitt"
              description="„Start“ lässt den Router tcpdump laufen und legt die .pcap.gz hier ab. Automatisch passiert nichts."
            />
          ) : (
            <div className="-mx-4 -mb-4">
              <Table columns={captureColumns(setDeleting, busy)} rows={rows} rowKey={(c) => c.id} />
            </div>
          )}
        </div>
      </Card>

      <Modal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title="Mitschnitt löschen?"
        size="sm"
        actions={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              iconName="trash"
              loading={busy}
              onClick={() => {
                if (deleting)
                  void act(() => api.deleteCapture(deleting.id), "Mitschnitt gelöscht.");
              }}
            >
              Löschen
            </Button>
          </>
        }
      >
        <p className="text-sm text-content-2">
          Die Datei vom {deleting ? fmtDateTime(deleting.createdAt) : ""} wird samt Eintrag
          entfernt. Es gibt keine zweite Kopie.
        </p>
      </Modal>
    </>
  );
}
